import ReflectionPanel from "./components/ReflectionPanel";
import React, {
  useState,
  useRef,
  useEffect,
  KeyboardEvent,
  useCallback,
} from "react";
import { v4 as uuidv4 } from "uuid";
import {
  Box,
  Button,
  Flex,
  HStack,
  Heading,
  IconButton,
  Image,
  Input,
  Spacer,
  Spinner,
  Text,
  Textarea,
  VStack,
  useColorMode,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import { MoonIcon, SunIcon } from "@chakra-ui/icons";

import {
  fetchPersona,
  fetchConversationHistory,
  sendPrompt,
  chatErrorMessage,
  approveAvatarReviewCandidate,
  apiUrl,
  fetchAvatarReviewPack,
  loadApiConfig,
  classifyIntent,
  startAlphaSession,
  ensureAlphaSession,
  downloadSessionMemory,
  authMe,
  authLogin,
  authGoogleLogin,
  authLogout,
  authProviders,
  authSignup,
  profileUpdate,
  fetchTourLegal,
  apiDisclaimerAck,
  getGoogleClientId,
} from "./services/api";
import type {
  AuthProviderSummary,
  AuthUser,
  AvatarReviewCandidate,
  AvatarReviewPack,
} from "./services/api";
import { FeedbackButton } from "./components/FeedbackButton";
import { AvatarToolsModal } from "./components/avatar/AvatarToolsModal";
import { MemoryManager } from "./components/memory/MemoryManager";
import { SoftMemory } from "./components/memory/SoftMemory";
import { RecollectionGraphFrame } from "./components/memory/RecollectionGraphFrame";
import {
  googleCsrfTokenForLogin,
  googleStateCookieDomainForHost,
  logGoogleAuthDiagnostic,
} from "./googleAuthCsrf";
import "./styles/chat.css";
import "./styles/avatar.css";
import { refreshAvatar } from "./lib/refreshAvatar";

interface ChatMessage {
  id: string;
  sender: "user" | "ai" | "system";
  content: string;
  reviewPack?: AvatarReviewPack;
  streaming?: boolean;
  interrupted?: boolean;
  memoryCaptureFailed?: boolean;
  reflectionDegraded?: boolean;
  error?: boolean;
}

type PrefillMessage = Pick<ChatMessage, "sender" | "content">;

interface ChatShellProps {
  prefillMessages?: PrefillMessage[];
  onPrefillConsumed?: () => void;
  onDownloadSession?: () => void;
  waitingForLegalChoice?: boolean;
  onLegalYes?: () => Promise<void> | void;
  onLegalNo?: (continueChat?: boolean) => Promise<void> | void;
  authUser?: AuthUser | null;
  onOpenAccount?: () => void;
  onOpenMemory?: () => void;
  onLogout?: () => Promise<void> | void;
}

const DEFAULT_STATIC_AVATAR_BASE = "https://api.lexicompanion.com/lexi/static/avatars/";
const DEFAULT_AVATAR_URL = "https://api.lexicompanion.com/lexi/account/avatar/default";

function resolveAvatarUrl(
  imagePath: string | null | undefined,
  fallbackBase = DEFAULT_STATIC_AVATAR_BASE
) {
  if (!imagePath) return `${fallbackBase}default.png`;
  if (imagePath.startsWith("http")) return imagePath;
  if (imagePath.startsWith("/lexi/account/avatar/")) return new URL(imagePath, fallbackBase).href;

  const normalized = imagePath.replace(/^\/+/, "");
  if (normalized.startsWith("lexi/static/")) {
    return `https://api.lexicompanion.com/${normalized}`;
  }
  if (normalized.startsWith("static/")) {
    return `https://api.lexicompanion.com/${normalized}`;
  }

  const suffix = imagePath.replace(/^.*avatars\//, "");
  const fileName = suffix || normalized;
  return `${fallbackBase}${fileName || "default.png"}`;
}

function mkId() {
  return `m_${uuidv4()}`;
}

function readCookieValue(name: string): string {
  if (typeof document === "undefined") return "";
  const prefix = `${encodeURIComponent(name)}=`;
  const entries = document.cookie ? document.cookie.split(";") : [];
  for (const entry of entries) {
    const trimmed = entry.trim();
    if (!trimmed.startsWith(prefix)) continue;
    return decodeURIComponent(trimmed.slice(prefix.length));
  }
  return "";
}

function googleStateCookieDomain(): string | undefined {
  if (typeof window === "undefined") return undefined;
  return googleStateCookieDomainForHost(window.location.hostname);
}

function mirrorCookieValue(name: string, value: string): void {
  if (typeof document === "undefined") return;
  const token = value.trim();
  if (!token) return;

  const secure = typeof window !== "undefined" && window.location.protocol === "https:" ? "; Secure" : "";
  const baseCookie = `${encodeURIComponent(name)}=${encodeURIComponent(token)}; path=/; SameSite=Lax${secure}`;
  document.cookie = baseCookie;

  if (typeof window === "undefined") return;
  const hostname = window.location.hostname.toLowerCase();
  if (!hostname.endsWith(".lexicompanion.com") && hostname !== "lexicompanion.com") {
    return;
  }

  document.cookie = `${baseCookie}; domain=.lexicompanion.com`;
}

function AvatarReviewPackPanel({
  pack,
  approving,
  onSelect,
}: {
  pack: AvatarReviewPack;
  approving: boolean;
  onSelect: (candidate: AvatarReviewCandidate) => void;
}) {
  const candidates = Array.isArray(pack.candidates) ? pack.candidates : [];
  if (!pack.id || candidates.length === 0) return null;

  return (
    <Box mt={3}>
      <Flex gap={3} wrap="wrap">
        {candidates.map((candidate) => {
          const blocked = isBlockedAvatarReviewCandidate(candidate);
          const disabled = approving || blocked;
          return (
            <Box key={`${pack.id}-${candidate.global_index}`} w="132px">
              <Image
                src={apiUrl(candidate.image_url)}
                alt={`Avatar option ${candidate.global_index + 1}`}
                w="132px"
                h="162px"
                objectFit="cover"
                borderRadius="sm"
                fallback={<Box w="132px" h="162px" bg="gray.100" />}
              />
              <Button
                mt={2}
                size="xs"
                colorScheme="pink"
                isLoading={approving}
                isDisabled={disabled}
                onClick={() => onSelect(candidate)}
                w="100%"
              >
                Use this
              </Button>
            </Box>
          );
        })}
      </Flex>
    </Box>
  );
}

function isBlockedAvatarReviewCandidate(candidate: AvatarReviewCandidate): boolean {
  return (
    (candidate.hard_fail_reasons?.length ?? 0) > 0 ||
    candidate.judge_status === "hard_fail"
  );
}

function reviewPackFromResponse(response: any): AvatarReviewPack | undefined {
  const embedded = response && typeof response === "object" ? response.review_pack : undefined;
  if (
    embedded &&
    typeof embedded === "object" &&
    Array.isArray(embedded.candidates) &&
    embedded.candidates.length > 0
  ) {
    return embedded as AvatarReviewPack;
  }
  return undefined;
}

function reviewPackIdFromText(text: string): string | undefined {
  const match = text.match(/\bReview pack:\s*([A-Za-z0-9_-]+)/i);
  return match?.[1];
}

export function ChatShell({
  prefillMessages,
  onPrefillConsumed,
  onDownloadSession,
  waitingForLegalChoice,
  onLegalYes,
  onLegalNo,
  authUser,
  onOpenAccount,
  onOpenMemory,
  onLogout,
}: ChatShellProps) {
  const { colorMode, toggleColorMode } = useColorMode();
  const bg = useColorModeValue("gray.50", "gray.800");
  const userBubbleBg = useColorModeValue("#ffd6ec", "pink.500");
  const aiBubbleBg = useColorModeValue("#ffffff", "rgba(255, 255, 255, 0.08)");
  const systemBubbleBg = useColorModeValue("#ffeaf4", "purple.600");
  const userBubbleColor = useColorModeValue("#2f001a", "white");
  const aiBubbleColor = useColorModeValue("#1f1626", "white");
  const systemBubbleColor = useColorModeValue("#2f001a", "white");
  const bubbleBorder = useColorModeValue(
    "1px solid rgba(255, 47, 160, 0.35)",
    "1px solid rgba(255, 105, 180, 0.4)"
  );
  const bubbleShadow = useColorModeValue(
    "0 10px 24px rgba(255, 47, 160, 0.18)",
    "0 0 16px rgba(255, 105, 180, 0.8)"
  );
  const composerBg = useColorModeValue("#fff0fa", "pink.500");
  const composerText = useColorModeValue("#2f001a", "white");
  const composerPlaceholder = useColorModeValue("#8d2950", "pink.200");
  const composerFieldBg = useColorModeValue("#ffffff", "rgba(255, 255, 255, 0.08)");
  const composerFieldBorder = useColorModeValue("1px solid #ff72d0", "1px solid hotpink");
  const composerShadow = useColorModeValue(
    "0 8px 18px rgba(255, 47, 160, 0.25)",
    "0 0 10px rgba(255, 105, 180, 0.6)"
  );

  const loadingVideoSrc = "/media/avatar-loading.mp4";
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [sharedPresence, setSharedPresence] = useState(false);
  const [input, setInput] = useState("");
  const staticAvatarBase = DEFAULT_STATIC_AVATAR_BASE;
  const [avatarUrl, setAvatarUrl] = useState<string | null>(DEFAULT_AVATAR_URL);
  const [avatarFlow, setAvatarFlow] = useState(false);
  const [avatarGenerating, setAvatarGenerating] = useState(false);
  const [loadingVideoErrored, setLoadingVideoErrored] = useState(false);
  const [avatarToolsOpen, setAvatarToolsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [approvingPackId, setApprovingPackId] = useState<string | null>(null);

  const chatEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const reviewPackHydrationRef = useRef<Set<string>>(new Set());
  const sendingRef = useRef(false);

  useEffect(() => {
    let active = true;
    // Restore only an empty transcript; never race an in-progress local turn.
    fetchConversationHistory().then(({ messages: history }) => {
      if (active && history.length) setMessages(prev => prev.length ? prev : history.map(m => ({
        id: m.message_id, sender: m.role === "user" ? "user" : "ai",
        content: m.content, interrupted: m.completion_status === "interrupted",
      })));
    }).catch(() => {});
    return () => { active = false; };
  }, []);

  const triggerAvatarRefresh = useCallback(async () => {
    try {
      const fresh = await refreshAvatar();
      if (fresh) {
        setAvatarUrl(fresh);
      }
    } catch (err) {
      console.warn("avatar refresh failed:", err);
    }
  }, [setAvatarUrl]);

  const handleAvatarUpdated = useCallback(
    (url: string) => {
      const resolved = resolveAvatarUrl(url, staticAvatarBase);
      const withoutCacheParams = resolved
        .replace(/([?&])(v|cb)=[^&]+/gi, "")
        .replace(/[?&]+$/, "");
      const finalUrl = `${withoutCacheParams}${
        withoutCacheParams.includes("?") ? "&" : "?"
      }v=${Date.now()}`;
      setAvatarUrl(finalUrl);
    },
    [staticAvatarBase]
  );

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    const missingReviewPacks = messages
      .filter((message) => message.sender === "ai" && !message.reviewPack && typeof message.content === "string")
      .map((message) => ({ messageId: message.id, packId: reviewPackIdFromText(message.content) }))
      .filter((item): item is { messageId: string; packId: string } => Boolean(item.packId));

    for (const { messageId, packId } of missingReviewPacks) {
      const hydrationKey = `${messageId}:${packId}`;
      if (reviewPackHydrationRef.current.has(hydrationKey)) continue;
      reviewPackHydrationRef.current.add(hydrationKey);
      void fetchAvatarReviewPack(packId)
        .then((pack) => {
          if (!Array.isArray(pack.candidates) || pack.candidates.length === 0) return;
          setMessages((prev) =>
            prev.map((message) =>
              message.id === messageId && !message.reviewPack ? { ...message, reviewPack: pack } : message
            )
          );
        })
        .catch((err) => {
          console.warn("[AvatarReview] Failed to hydrate review pack", err);
        });
    }
  }, [messages]);

  useEffect(() => {
    if (!prefillMessages || prefillMessages.length === 0) {
      return;
    }
    setMessages((prev) => [
      ...prev,
      ...prefillMessages.map((msg) => ({ id: mkId(), ...msg })),
    ]);
    onPrefillConsumed?.();
  }, [prefillMessages, onPrefillConsumed]);

  useEffect(() => {
    let mounted = true;
    setAvatarUrl(DEFAULT_AVATAR_URL);
    (async () => { const url = await refreshAvatar(); if (mounted && url) setAvatarUrl(url); })()
      .catch(err => console.warn("avatar hydration failed", err));
    return () => { mounted = false; };
  }, [authUser?.id]);

  const startNewConversation = async () => {
    try {
      await startAlphaSession();
      setMessages([]);
      setInput("");
    } catch (err) { console.warn("new conversation failed", err); }
  };

  const appendMessage = useCallback(
    (msg: Omit<ChatMessage, "id"> & { id?: string }) => {
    setMessages((prev) => [...prev, { id: msg.id ?? mkId(), ...msg }]);
    },
    []
  );

  const handleSelectReviewCandidate = useCallback(
    async (pack: AvatarReviewPack, candidate: AvatarReviewCandidate) => {
      if (!pack.id || candidate.global_index === undefined) return;
      if (isBlockedAvatarReviewCandidate(candidate)) {
        appendMessage({ sender: "ai", content: "[error] That avatar option did not pass review and can't be used.", error: true });
        return;
      }
      setApprovingPackId(pack.id);
      try {
        const approval = await approveAvatarReviewCandidate(pack.id, candidate.global_index);
        const publishedUrl =
          approval?.operator_state?.published_avatar_url ??
          approval?.approval?.published_avatar_url ??
          approval?.published_avatar_url;
        if (typeof publishedUrl === "string" && publishedUrl) {
          handleAvatarUpdated(publishedUrl);
        } else {
          await triggerAvatarRefresh();
        }
        setMessages((prev) =>
          prev.map((message) =>
            message.reviewPack?.id === pack.id
              ? {
                  ...message,
                  content: "Avatar updated from the selected review option.",
                  reviewPack: undefined,
                }
              : message
          )
        );
      } catch (err) {
        const message = err instanceof Error ? err.message : "Avatar approval failed";
        appendMessage({ sender: "ai", content: `[error] ${message}`, error: true });
      } finally {
        setApprovingPackId(null);
      }
    },
    [appendMessage, handleAvatarUpdated, triggerAvatarRefresh]
  );

  const handleSend = useCallback(async () => {
    const text = input.trim();
    if (!text || loading || sendingRef.current) return;
    sendingRef.current = true;
    setLoading(true);
    try {
      setInput("");
      inputRef.current?.focus();
      appendMessage({ sender: "user", content: text });

      if (waitingForLegalChoice) {
        const trimmed = text.trim().toLowerCase();
        if (trimmed === "yes" || trimmed === "y") {
          await onLegalYes?.();
          return;
        }
        if (trimmed === "no" || trimmed === "n") {
          await onLegalNo?.(false);
          return;
        }
        await onLegalNo?.(true);
      }

      // --- 1) Retire the legacy trait-by-trait chat flow; avatar changes go through Avatar Director.
      if (avatarFlow) {
        setAvatarFlow(false);
      }

      // --- 2) Otherwise ask the backend "chat or avatar_flow?" ---
      let intent = "chat";
      try {
        ({ intent } = await classifyIntent(text));
        console.log("🕵️ Intent is", intent);
      } catch (e) {
        console.warn("Intent API failed, defaulting to chat", e);
      }

      if (intent === "avatar_flow" || intent === "new_look") {
        setAvatarFlow(false);
      }

      if (intent === "avatar_edit") {
        setAvatarFlow(false);
      }

      if (intent === "describe_avatar") {
        const personaData = await fetchPersona();
        if (!personaData || !personaData.traits) {
          appendMessage({ sender: "ai", content: "I'm not sure how I look right now..." });
          return;
        }
        const traits = personaData.traits;

        const desc = Object.entries(traits)
          .map(([k, v]) => `${k.replace("_", " ")}: ${v}`)
          .join(", ");
        appendMessage({ sender: "ai", content: `Here's how I look right now: ${desc}.` });
        return;
      }

      // --- 3) Fallback to your normal LLM chat ---
      const aiId = mkId();
      appendMessage({ id: aiId, sender: "ai", content: "", streaming: true });
      setLoading(true);
      let streamed = "";
      try {
        const res = await sendPrompt({
          prompt: text,
          intent,
          requestId: aiId.slice(2),
          onChunk(delta) {
            streamed += delta;
            setMessages((prev) =>
              prev.map((m) =>
                m.id === aiId ? { ...m, content: streamed, streaming: true } : m
              )
            );
          },
        });
        const finalText =
          (typeof res?.cleaned === "string" && res.cleaned.trim()) ||
          streamed.trim() ||
          "[no response]";
        let reviewPack = reviewPackFromResponse(res);
        if (!reviewPack) {
          const packId = reviewPackIdFromText(finalText);
          if (packId) {
            try {
              const fetchedPack = await fetchAvatarReviewPack(packId);
              if (Array.isArray(fetchedPack.candidates) && fetchedPack.candidates.length > 0) {
                reviewPack = fetchedPack;
              }
            } catch (err) {
              console.warn("[AvatarReview] Failed to fetch review pack", err);
            }
          }
        }
        setMessages((prev) =>
          prev.map((m) =>
            m.id === aiId
              ? {
                  ...m,
                  content: finalText,
                  interrupted: res?.completion_status === "interrupted" || res?.finish_reason === "length",
                  reflectionDegraded: res?.reflection_status === "degraded",
                  error: res?.completion_status === "failed" || res?.technical_error === true,
                  memoryCaptureFailed: Object.values(res?.memory_capture || {}).includes("failed"),
                  reviewPack,
                  streaming: false,
                }
              : res?.reflection_status === "ready" ? { ...m, reflectionDegraded: false } : m
          )
        );
      } catch (err) {
        const message = chatErrorMessage(err);
        setMessages((prev) =>
          prev.map((m) =>
            m.id === aiId
              ? {
                  ...m,
                  content: streamed || message,
                  interrupted: Boolean(streamed),
                  streaming: false,
                  error: true,
                }
              : m
          )
        );
      }
    } finally {
      sendingRef.current = false;
      setLoading(false);
    }
  }, [
    input,
    loading,
    avatarFlow,
    appendMessage,
    waitingForLegalChoice,
    onLegalYes,
    onLegalNo,
  ]);

//import { useEffect } from "react";
//import {
//  lexiOnboardingMessage,
//  nsfwFallbackMessage,
//  isTooHot,
//  shouldShowOnboarding,
//  markOnboardingShown
//} from "../../../utils/lexiOnboarding";

//function App() {
//  useEffect(() => {
//    if (shouldShowOnboarding()) {
      // Push Lexi's message into chat stream
//      addAssistantMessage(lexiOnboardingMessage); // your method here
//    markOnboardingShown();
//    }
//  }, []);
 
// return <ChatUI />;
//}


  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      <Flex
        className="appShell"
        direction="column"
        minH="var(--app-dvh)"
        bg={bg}
        fontFamily="'Nunito', sans-serif"
      >
        <HStack
          className="fixedBar"
          p={3}
          bg="pink.500"
          color="white"
          boxShadow="0 2px 12px rgba(255, 105, 180, 0.6)"
          data-tour="header"
        >
          <Box fontWeight="bold" fontSize="xl">
            Lexi Chat
          </Box>
          <Spacer />
          {onDownloadSession && (
            <Button size="lg" variant="outline" color="white" onClick={onDownloadSession}>
              Download session
            </Button>
          )}
          {authUser && <Button size="sm" onClick={startNewConversation} isDisabled={loading || avatarGenerating}>New conversation</Button>}
          {authUser && onOpenAccount && (
            <Button size="lg" variant="outline" color="white" onClick={onOpenAccount}>
              {authUser.display_name || authUser.email || "Account"}
            </Button>
          )}
          {authUser && onOpenMemory && (
            <Button size="lg" variant="outline" color="white" onClick={onOpenMemory}>
              Memory
            </Button>
          )}
          {authUser && onLogout && (
            <Button size="lg" variant="outline" color="white" onClick={() => void onLogout()}>
              Logout
            </Button>
          )}
          <FeedbackButton />
          <IconButton
            aria-label="Toggle dark mode"
            icon={colorMode === "light" ? <MoonIcon /> : <SunIcon />}
            onClick={toggleColorMode}
            variant="ghost"
            color="white"
            size="lg"
            minW={11}
            minH={11}
          />
        </HStack>

        {authUser && <ReflectionPanel key={authUser.id} accountId={authUser.id} onPresence={setSharedPresence} />}
        <Box flex="1" px={4} pt={4} pb={2} minH="0">
          <Box className={`chat-layout with-avatar${sharedPresence ? " shared-presence" : ""}`} h="100%" minH="0" w="100%">
            <Box as="aside" className="avatar-pane" data-tour="avatar">
              {avatarUrl && (
                <Box
                  className="avatar-card"
                  borderRadius="2xl"
                  border="2px solid hotpink"
                  overflow="hidden"
                  animation="pulseGlow 2s infinite"
                  boxShadow="0 0 24px rgba(255, 64, 128, 0.25)"
                  sx={{
                    "@keyframes pulseGlow": {
                      "0%": { boxShadow: "0 0 8px rgba(255, 105, 180, 0.3)" },
                      "50%": { boxShadow: "0 0 18px rgba(255, 105, 180, 0.8)" },
                      "100%": { boxShadow: "0 0 8px rgba(255, 105, 180, 0.3)" },
                    },
                  }}
                >
                  <Box position="relative">
                    {avatarGenerating && !loadingVideoErrored ? (
                      <Box
                        as="video"
                        className="avatar-loading-video"
                        src={loadingVideoSrc}
                        autoPlay
                        muted
                        loop
                        playsInline
                        preload="none"
                        poster={avatarUrl ?? undefined}
                        aria-label="Avatar generating animation"
                        onError={() => setLoadingVideoErrored(true)}
                      />
                    ) : (
                      <Image
                        data-avatar
                        src={avatarUrl}
                        alt="Lex avatar"
                        w="100%"
                        loading="lazy"
                        decoding="async"
                        display="block"
                        objectFit="cover"
                        aspectRatio="3 / 4"
                        sizes="(max-width: 900px) 100vw, 280px"
                        backdropFilter="blur(4px)"
                      />
                    )}
                    {avatarGenerating && (
                      <Flex
                        className="avatar-loading-overlay"
                        align="flex-end"
                        justify="space-between"
                        px={3}
                        py={2}
                        gap={3}
                      >
                        <Text fontWeight="bold">Generating your new look…</Text>
                        <Spinner size="sm" thickness="3px" color="pink.200" />
                      </Flex>
                    )}
                  </Box>
                </Box>
              )}
              <Button
                mt={3}
                variant="outline"
                colorScheme="pink"
                size="lg"
                onClick={() => setAvatarToolsOpen(true)}
                data-tour="gallery"
              >
                Open avatar tools
              </Button>
            </Box>

            <Box
              as="main"
              className="chat-pane"
              display="flex"
              flexDirection="column"
              h="100%"
              minH="0"
            >
              <Box
                className="chat-scroll"
                flex="1"
                overflowY="auto"
                minH="0"
                pt={2}
                pb={2}
                px={2}
                css={{
                  scrollbarColor: "hotpink transparent",
                  scrollbarWidth: "thin",
                }}
              >
                <VStack spacing={4} align="stretch">
                  {messages.map((m) => {
                    const hasContent =
                      typeof m.content === "string" && m.content.trim().length > 0;
                    if (!hasContent && !m.streaming) return null;

                    const isUser = m.sender === "user";
                    const isAi = m.sender === "ai";
                    const bubbleBg = isUser ? userBubbleBg : isAi ? aiBubbleBg : systemBubbleBg;
                    const bubbleColor = isUser
                      ? userBubbleColor
                      : isAi
                      ? aiBubbleColor
                      : systemBubbleColor;

                    return (
                      <Box
                        key={m.id}
                        className="bubble"
                        alignSelf={
                          m.sender === "user"
                            ? "flex-end"
                            : m.sender === "ai"
                            ? "flex-start"
                            : "center"
                        }
                        bg={bubbleBg}
                        color={bubbleColor}
                        px={5}
                        py={3}
                        borderRadius="xl"
                        maxW="80%"
                        whiteSpace="pre-wrap"
                        opacity={m.streaming ? 0.9 : 1}
                        border={m.error ? "1px solid red" : bubbleBorder}
                        boxShadow={bubbleShadow}
                        backdropFilter="blur(10px)"
                        transition="all 0.2s ease-in-out"
                      >
                        {hasContent && <Box as="span">{m.content}</Box>}
                        {m.reviewPack && (
                          <AvatarReviewPackPanel
                            pack={m.reviewPack}
                            approving={approvingPackId === m.reviewPack.id}
                            onSelect={(candidate) =>
                              void handleSelectReviewCandidate(m.reviewPack as AvatarReviewPack, candidate)
                            }
                          />
                        )}
                        {m.interrupted && <Text fontSize="xs" color="orange.300">Reply interrupted. You can ask Lexi to continue.</Text>}
                        {m.reflectionDegraded && <Text fontSize="xs" color="orange.300">Reflection is temporarily limited. You can keep chatting.</Text>}
                        {m.memoryCaptureFailed && <Text fontSize="xs" color="orange.300">Durable memory could not confirm saving this turn. This reply does not confirm it was remembered.</Text>}
                        {m.streaming && (
                          <Flex
                            mt={hasContent ? 2 : 0}
                            justify={hasContent ? "flex-end" : "center"}
                            aria-label="Lexi is still typing"
                          >
                            <Spinner size="xs" label="" />
                          </Flex>
                        )}
                      </Box>
                    );
                  })}

                  <div ref={chatEndRef} />
                </VStack>
              </Box>
            </Box>
          </Box>
        </Box>

        <SoftMemory />
        <RecollectionGraphFrame />

        <HStack
          className="fixedBar"
          p={3}
          bg={composerBg}
          boxShadow={composerShadow}
          data-tour="composer"
        >
          <Textarea
            flex="1"
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type a message... (Enter = send, Shift+Enter = newline)"
            resize="none"
            disabled={loading}
            size="lg"
            minH="56px"
            bg={composerFieldBg}
            color={composerText}
            _placeholder={{ color: composerPlaceholder }}
            border={composerFieldBorder}
            boxShadow={composerShadow}
            backdropFilter="blur(6px)"
          />
          <Button
            onClick={handleSend}
            colorScheme="pink"
            size="lg"
            isDisabled={!input.trim() || loading}
            boxShadow="0 0 14px rgba(255, 105, 180, 0.7)"
          >
            Send
          </Button>
        </HStack>
      </Flex>
      <AvatarToolsModal
        isOpen={avatarToolsOpen}
        onClose={() => setAvatarToolsOpen(false)}
        currentAvatarUrl={avatarUrl ?? undefined}
        onAvatarUpdated={handleAvatarUpdated}
        onGenerationStart={() => {
          setLoadingVideoErrored(false);
          setAvatarGenerating(true);
        }}
        onGenerationEnd={() => setAvatarGenerating(false)}
      />
    </>
  );
}

type AuthMode = "login" | "signup";
type AccountPane = "profile" | "memory";
const CHAT_LEGAL_PROMPT =
  "I'm still new at this. Want the boring legal stuff before we dive in? Reply \"yes\" or \"no\".";
const CHAT_LEGAL_NO_REPLY =
  "Okay fair, then please don't ask me to do things that could put me in a bad situation later.";

export default function App() {
  const toast = useToast();
  const [sessionStarted, setSessionStarted] = useState(false);
  const [sessionStarting, setSessionStarting] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [accountPane, setAccountPane] = useState<AccountPane>("profile");
  const [authMode, setAuthMode] = useState<AuthMode>("login");
  const [account, setAccount] = useState<AuthUser | null>(null);
  const [providers, setProviders] = useState<AuthProviderSummary[]>([]);
  const [providersChecked, setProvidersChecked] = useState(false);
  const [prefillMessages, setPrefillMessages] = useState<PrefillMessage[]>([]);
  const [waitingForLegalChoice, setWaitingForLegalChoice] = useState(false);
  const [googleReady, setGoogleReady] = useState(false);
  const [googleAuthLoading, setGoogleAuthLoading] = useState(false);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [username, setUsername] = useState("");
  const [signupDisclaimerAck, setSignupDisclaimerAck] = useState(false);

  const [profileDisplayName, setProfileDisplayName] = useState("");
  const [profileUsername, setProfileUsername] = useState("");
  const googleButtonRef = useRef<HTMLDivElement | null>(null);

  const startFlow = useCallback(async () => {
    setSessionStarting(true);
    try {
      await ensureAlphaSession();
      setSessionStarted(true);
    } catch (err) {
      console.error("alpha onboarding init failed:", err);
      setSessionStarted(true);
    } finally {
      setSessionStarting(false);
    }
  }, []);

  useEffect(() => {
    if (!authChecked || sessionStarted || sessionStarting) return;
    void startFlow();
  }, [authChecked, sessionStarted, sessionStarting, startFlow]);

  const hydrateAuthState = useCallback(async () => {
    try {
      const me = await authMe();
      if (me.authenticated && me.user) {
        setAccount(me.user);
        setProfileDisplayName(me.user.display_name || "");
        setProfileUsername(me.user.username || "");
        setShowChat(true);
        setAccountPane("profile");
      } else {
        setAccount(null);
      }
    } catch (err) {
      console.warn("auth/me failed:", err);
      setAccount(null);
    } finally {
      setAuthChecked(true);
    }
  }, []);

  useEffect(() => {
    void hydrateAuthState();
    void authProviders()
      .then((resp) => setProviders(resp.providers || []))
      .catch(() =>
        setProviders([
          { name: "password", enabled: true, status: "active" },
          { name: "google", enabled: false, status: "coming_soon" },
        ])
      )
      .finally(() => setProvidersChecked(true));
  }, [hydrateAuthState]);

  const queuePrefill = useCallback((message: PrefillMessage | PrefillMessage[]) => {
    setPrefillMessages((prev) => [...prev, ...(Array.isArray(message) ? message : [message])]);
  }, []);

  const beginChatLegalChoice = useCallback(() => {
    setWaitingForLegalChoice(true);
    queuePrefill({ sender: "ai", content: CHAT_LEGAL_PROMPT });
  }, [queuePrefill]);

  const handlePrefillConsumed = useCallback(() => {
    setPrefillMessages([]);
  }, []);

  const handleLegalYes = useCallback(async () => {
    setWaitingForLegalChoice(false);
    try {
      const legal = await fetchTourLegal();
      const text = (legal?.text || "").trim();
      queuePrefill(
        text
          ? [
              { sender: "ai", content: "Alright, here it is." },
              { sender: "ai", content: text },
              { sender: "ai", content: "Thanks for reading. I'm ready whenever you are." },
            ]
          : [
              {
                sender: "ai",
                content:
                  "I couldn't pull the full legal text right now, but short version: I'm still experimental, may be wrong, and I can't replace professional advice.",
              },
            ]
      );
    } catch {
      queuePrefill({
        sender: "ai",
        content:
          "I couldn't pull the full legal text right now, but short version: I'm still experimental, may be wrong, and I can't replace professional advice.",
      });
    }

    if (account?.id) {
      try {
        await apiDisclaimerAck(account.id, true, "chat_legal_v1");
      } catch {
        // best-effort telemetry/ack path only
      }
    }
  }, [account?.id, queuePrefill]);

  const handleLegalNo = useCallback(
    async (continueChat?: boolean) => {
      setWaitingForLegalChoice(false);
      if (!continueChat) {
        queuePrefill({ sender: "ai", content: CHAT_LEGAL_NO_REPLY });
      }
      if (account?.id) {
        try {
          await apiDisclaimerAck(account.id, false, "chat_legal_v1");
        } catch {
          // best-effort telemetry/ack path only
        }
      }
    },
    [account?.id, queuePrefill]
  );

  const handleDownloadSession = useCallback(async () => {
    try {
      const { blob, filename } = await downloadSessionMemory();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename || "memory.jsonl";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      const message = err instanceof Error ? err.message : "download failed";
      toast({
        status: "error",
        title: "couldn't download session",
        description: message,
        duration: 4000,
        isClosable: true,
      });
    }
  }, [toast]);

  const handleAuthSubmit = useCallback(async () => {
    setAuthLoading(true);
    try {
      if (authMode === "signup") {
        if (!signupDisclaimerAck) {
          throw new Error("Please accept the disclaimer to create your account.");
        }
        if (password !== confirmPassword) {
          throw new Error("Passwords do not match.");
        }
        if (!displayName.trim()) {
          throw new Error("Please tell Lexi what to call you.");
        }
        const resp = await authSignup({
          email,
          password,
          display_name: displayName.trim(),
          username: username.trim() || undefined,
          disclaimer_acknowledged: signupDisclaimerAck,
        });
        if (!resp.authenticated || !resp.user) {
          throw new Error("Unable to sign up.");
        }
        setAccount(resp.user);
        setProfileDisplayName(resp.user.display_name || "");
        setProfileUsername(resp.user.username || "");
        setShowChat(true);
        setAccountPane("profile");
        queuePrefill([{ sender: "ai", content: `Hi ${resp.user.display_name || "there"} - ready when you are.` }]);
        beginChatLegalChoice();
      } else {
        const resp = await authLogin({ email, password });
        if (!resp.authenticated || !resp.user) {
          throw new Error("Unable to sign in.");
        }
        setAccount(resp.user);
        setProfileDisplayName(resp.user.display_name || "");
        setProfileUsername(resp.user.username || "");
        setShowChat(true);
        setAccountPane("profile");
        beginChatLegalChoice();
      }
      setPassword("");
      setConfirmPassword("");
    } catch (err: any) {
      const message = err instanceof Error ? err.message : "Authentication failed.";
      toast({
        status: "error",
        title: authMode === "signup" ? "couldn't sign up" : "couldn't sign in",
        description: message,
        duration: 3500,
        isClosable: true,
      });
    } finally {
      setAuthLoading(false);
    }
  }, [
    authMode,
    signupDisclaimerAck,
    password,
    confirmPassword,
    displayName,
    email,
    username,
    queuePrefill,
    beginChatLegalChoice,
    toast,
  ]);

  const handleGoogleCredential = useCallback(
    async (credential: string, gCsrfToken?: string) => {
      logGoogleAuthDiagnostic("backend.post.start", {
        credential_present: Boolean(credential),
        credential_len: credential.length,
        g_csrf_token_cookie_present: Boolean((gCsrfToken || "").trim()),
        g_csrf_token_cookie_len: (gCsrfToken || "").trim().length,
      });
      if (!credential) {
        toast({
          status: "error",
          title: "google sign-in failed",
          description: "No credential was returned.",
          duration: 3500,
          isClosable: true,
        });
        return;
      }
      setGoogleAuthLoading(true);
      try {
        const resp = await authGoogleLogin({
          credential,
          g_csrf_token: (gCsrfToken || "").trim() || undefined,
        }, {
          onResponseStatus: (status) => {
            logGoogleAuthDiagnostic("backend.post.response", {
              backend_status: status,
            });
          },
        });
        if (!resp.authenticated || !resp.user) {
          throw new Error("Unable to sign in with Google.");
        }
        setAccount(resp.user);
        setProfileDisplayName(resp.user.display_name || "");
        setProfileUsername(resp.user.username || "");
        setShowChat(true);
        setAccountPane("profile");
        beginChatLegalChoice();
        setPassword("");
        setConfirmPassword("");
      } catch (err: any) {
        const message = err instanceof Error ? err.message : "Google authentication failed.";
        logGoogleAuthDiagnostic("backend.post.error", {
          error_class: err instanceof Error ? err.name : typeof err,
        });
        toast({
          status: "error",
          title: "couldn't sign in with google",
          description: message,
          duration: 3500,
          isClosable: true,
        });
      } finally {
        setGoogleAuthLoading(false);
      }
    },
    [beginChatLegalChoice, toast]
  );

  const googleProvider = providers.find((provider) => provider.name === "google");
  const googleClientId = getGoogleClientId();
  const googleEnabled = Boolean(googleProvider?.enabled && googleClientId);

  useEffect(() => {
    if (account) {
      logGoogleAuthDiagnostic("gis.terminal", {
        provider_enabled: Boolean(googleProvider?.enabled),
        client_id_present: Boolean(googleClientId),
        client_id_len: googleClientId.length,
        reason: "already_authenticated",
      });
      setGoogleReady(false);
      if (googleButtonRef.current) {
        googleButtonRef.current.innerHTML = "";
      }
      return;
    }

    if (!providersChecked) {
      logGoogleAuthDiagnostic("gis.pending", {
        provider_loading: true,
        client_id_present: Boolean(googleClientId),
        client_id_len: googleClientId.length,
        reason: "provider_loading",
      });
      setGoogleReady(false);
      return;
    }

    if (!googleEnabled) {
      logGoogleAuthDiagnostic("gis.unavailable", {
        provider_enabled: Boolean(googleProvider?.enabled),
        client_id_present: Boolean(googleClientId),
        client_id_len: googleClientId.length,
        reason: "disabled_or_missing_client_id",
      });
      setGoogleReady(false);
      if (googleButtonRef.current) {
        googleButtonRef.current.innerHTML = "";
      }
      return;
    }

    let cancelled = false;
    const scriptSelector = 'script[data-lex-google-gsi="1"]';

    const configureGoogle = () => {
      if (cancelled) return;
      const googleApi = (window as any).google?.accounts?.id;
      if (!googleApi || !googleButtonRef.current) {
        logGoogleAuthDiagnostic("gis.unavailable", {
          google_api_present: Boolean(googleApi),
          google_button_present: Boolean(googleButtonRef.current),
          client_id_present: Boolean(googleClientId),
          client_id_len: googleClientId.length,
          reason: "api_or_button_missing",
        });
        setGoogleReady(false);
        return;
      }
      googleButtonRef.current.innerHTML = "";
      const stateCookieDomain = googleStateCookieDomain();
      logGoogleAuthDiagnostic("gis.initialize.start", {
        client_id_present: Boolean(googleClientId),
        client_id_len: googleClientId.length,
        state_cookie_domain: stateCookieDomain || "none",
      });
      googleApi.initialize({
        client_id: googleClientId,
        ...(stateCookieDomain ? { state_cookie_domain: stateCookieDomain } : {}),
        callback: (response: any) => {
          const credential = typeof response?.credential === "string" ? response.credential : "";
          const cookieCsrfToken = readCookieValue("g_csrf_token");
          const responseCsrfToken = typeof response?.g_csrf_token === "string" ? response.g_csrf_token : "";
          const gCsrfToken = googleCsrfTokenForLogin({
            cookieToken: cookieCsrfToken,
            responseToken: responseCsrfToken,
          });
          logGoogleAuthDiagnostic("gis.callback.invoked", {
            credential_present: Boolean(credential),
            credential_len: credential.length,
            g_csrf_token_cookie_present: Boolean(cookieCsrfToken),
            g_csrf_token_cookie_len: cookieCsrfToken.length,
            g_csrf_token_response_present: Boolean(responseCsrfToken),
            g_csrf_token_response_len: responseCsrfToken.length,
          });
          mirrorCookieValue("g_csrf_token", gCsrfToken);
          void handleGoogleCredential(credential, gCsrfToken);
        },
      });
      logGoogleAuthDiagnostic("gis.initialize.success", {
        client_id_present: Boolean(googleClientId),
        client_id_len: googleClientId.length,
        state_cookie_domain: stateCookieDomain || "none",
      });
      logGoogleAuthDiagnostic("gis.button.render.start", {
        button_present: Boolean(googleButtonRef.current),
      });
      googleApi.renderButton(googleButtonRef.current, {
        theme: "outline",
        size: "large",
        shape: "pill",
        text: "continue_with",
        width: 320,
      });
      logGoogleAuthDiagnostic("gis.button.render.success", {
        button_present: Boolean(googleButtonRef.current),
      });
      // TODO(auth-google-gis-diagnostics): keep One Tap disabled until FedCM/prompt noise is intentionally reintroduced.
      logGoogleAuthDiagnostic("gis.prompt.disabled", {
        reason: "button_only",
      });
      setGoogleReady(true);
    };

    if ((window as any).google?.accounts?.id) {
      logGoogleAuthDiagnostic("gis.script.loaded", {
        script_existing: true,
      });
      configureGoogle();
      return () => {
        cancelled = true;
      };
    }

    let script = document.querySelector(scriptSelector) as HTMLScriptElement | null;
    const onLoad = () => {
      logGoogleAuthDiagnostic("gis.script.loaded", {
        script_existing: Boolean(script),
      });
      configureGoogle();
    };
    const onError = () => {
      logGoogleAuthDiagnostic("gis.script.unavailable", {
        script_existing: Boolean(script),
      });
      if (!cancelled) setGoogleReady(false);
    };

    if (!script) {
      script = document.createElement("script");
      script.src = "https://accounts.google.com/gsi/client";
      script.async = true;
      script.defer = true;
      script.setAttribute("data-lex-google-gsi", "1");
      script.addEventListener("load", onLoad);
      script.addEventListener("error", onError);
      document.head.appendChild(script);
    } else {
      script.addEventListener("load", onLoad);
      script.addEventListener("error", onError);
    }

    return () => {
      cancelled = true;
      script?.removeEventListener("load", onLoad);
      script?.removeEventListener("error", onError);
    };
  }, [account, googleClientId, googleEnabled, googleProvider?.enabled, handleGoogleCredential, providersChecked]);

  const handleSaveProfile = useCallback(async () => {
    if (!account) return;
    setProfileSaving(true);
    try {
      const resp = await profileUpdate({
        display_name: profileDisplayName.trim() || undefined,
        username: profileUsername.trim() || undefined,
      });
      setAccount(resp.user);
      setProfileDisplayName(resp.user.display_name || "");
      setProfileUsername(resp.user.username || "");
      toast({
        status: "success",
        title: "profile updated",
        duration: 2200,
        isClosable: true,
      });
    } catch (err: any) {
      const message = err instanceof Error ? err.message : "Profile update failed.";
      toast({
        status: "error",
        title: "couldn't save profile",
        description: message,
        duration: 3200,
        isClosable: true,
      });
    } finally {
      setProfileSaving(false);
    }
  }, [account, profileDisplayName, profileUsername, toast]);

  useEffect(() => {
    if (account?.id) {
      void ensureAlphaSession().catch(err => console.warn("account conversation resume failed", err));
    }
  }, [account?.id]);

  const handleLogout = useCallback(async () => {
    try {
      await authLogout();
    } catch (err) {
      console.warn("logout failed:", err);
    }
    setAccount(null);
    setShowChat(false);
    setAccountPane("profile");
    setEmail("");
    setPassword("");
    setConfirmPassword("");
    setDisplayName("");
    setUsername("");
    setWaitingForLegalChoice(false);
  }, []);

  if (!authChecked || !sessionStarted) {
    return (
      <Flex className="appShell" align="center" justify="center" minH="var(--app-dvh)" px={6}>
        <VStack spacing={4}>
          <Spinner size="lg" />
          <Text>Starting Lexi...</Text>
        </VStack>
      </Flex>
    );
  }

  if (account && showChat) {
    return (
      <ChatShell
        key={account.id}
        prefillMessages={prefillMessages}
        onPrefillConsumed={handlePrefillConsumed}
        onDownloadSession={handleDownloadSession}
        waitingForLegalChoice={waitingForLegalChoice}
        onLegalYes={handleLegalYes}
        onLegalNo={handleLegalNo}
        authUser={account}
        onOpenAccount={() => {
          setAccountPane("profile");
          setShowChat(false);
        }}
        onOpenMemory={() => {
          setAccountPane("memory");
          setShowChat(false);
        }}
        onLogout={handleLogout}
      />
    );
  }

  if (account && !showChat) {
    return (
      <Flex className="appShell" align="center" justify="center" minH="var(--app-dvh)" px={6}>
        <Box
          maxW={accountPane === "memory" ? "1320px" : "760px"}
          w="100%"
          bg="whiteAlpha.900"
          _dark={{ bg: "gray.800" }}
          borderRadius="2xl"
          boxShadow="xl"
          p={{ base: 6, md: 10 }}
        >
          <VStack spacing={6} align="stretch">
            <HStack justify="space-between" align="start" flexWrap="wrap" spacing={4}>
              <VStack align="start" spacing={1}>
                <Heading size="lg">{accountPane === "memory" ? "Memory Controls" : "Account"}</Heading>
                <Text color="gray.600" _dark={{ color: "gray.300" }}>
                  Signed in as {account.email || account.display_name || account.id}
                </Text>
              </VStack>
              <HStack spacing={2}>
                <Button
                  size="sm"
                  colorScheme={accountPane === "profile" ? "pink" : undefined}
                  variant={accountPane === "profile" ? "solid" : "outline"}
                  onClick={() => setAccountPane("profile")}
                >
                  Profile
                </Button>
                <Button
                  size="sm"
                  colorScheme={accountPane === "memory" ? "pink" : undefined}
                  variant={accountPane === "memory" ? "solid" : "outline"}
                  onClick={() => setAccountPane("memory")}
                >
                  Memory
                </Button>
              </HStack>
            </HStack>

            {accountPane === "profile" ? (
              <VStack spacing={5} align="stretch">
                <Input
                  placeholder="What should Lexi call you?"
                  value={profileDisplayName}
                  onChange={(e) => setProfileDisplayName(e.target.value)}
                  size="lg"
                />
                <Input
                  placeholder="Username (optional)"
                  value={profileUsername}
                  onChange={(e) => setProfileUsername(e.target.value)}
                  size="lg"
                />
                <Box>
                  <Text fontWeight="semibold" mb={2}>
                    Connected providers
                  </Text>
                  <VStack align="stretch" spacing={2}>
                    {(providers.length ? providers : [{ name: "password", enabled: true, status: "active" }]).map(
                      (provider) => (
                        <HStack key={provider.name} justify="space-between">
                          <Text>{provider.name}</Text>
                          <Text fontSize="sm" color="gray.500">
                            {provider.status}
                          </Text>
                        </HStack>
                      )
                    )}
                  </VStack>
                </Box>
                <Button variant="outline" isDisabled>
                  {googleProvider?.enabled ? "Google provider active" : "Google sign-in unavailable"}
                </Button>
              </VStack>
            ) : (
              <MemoryManager />
            )}

            <HStack spacing={4} flexWrap="wrap">
              {accountPane === "profile" ? (
                <Button colorScheme="pink" onClick={() => void handleSaveProfile()} isLoading={profileSaving}>
                  Save profile
                </Button>
              ) : null}
              <Button variant="outline" onClick={() => setShowChat(true)}>
                Enter chat
              </Button>
              <Button variant="ghost" onClick={() => void handleLogout()}>
                Logout
              </Button>
            </HStack>
          </VStack>
        </Box>
      </Flex>
    );
  }

  return (
    <Flex className="appShell" align="center" justify="center" minH="var(--app-dvh)" px={6}>
      <Box
        maxW="720px"
        w="100%"
        bg="whiteAlpha.900"
        _dark={{ bg: "gray.800" }}
        borderRadius="2xl"
        boxShadow="xl"
        p={{ base: 6, md: 10 }}
      >
        <VStack spacing={5} align="stretch">
          <Heading size="lg">{authMode === "signup" ? "Create your Lexi account" : "Sign in to Lexi"}</Heading>
          <HStack spacing={4}>
            <Button
              colorScheme={authMode === "login" ? "pink" : undefined}
              variant={authMode === "login" ? "solid" : "outline"}
              onClick={() => setAuthMode("login")}
            >
              Login
            </Button>
            <Button
              colorScheme={authMode === "signup" ? "pink" : undefined}
              variant={authMode === "signup" ? "solid" : "outline"}
              onClick={() => setAuthMode("signup")}
            >
              Sign up
            </Button>
          </HStack>

          {authMode === "signup" && (
            <>
              <Input
                placeholder="What should Lexi call you?"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                size="lg"
              />
              <Input
                placeholder="Username (optional)"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                size="lg"
              />
            </>
          )}

          <Input
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            size="lg"
            type="email"
            autoComplete="email"
          />
          <Input
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            size="lg"
            type="password"
            autoComplete={authMode === "signup" ? "new-password" : "current-password"}
          />
          {authMode === "signup" && (
            <>
              <Input
                placeholder="Confirm password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                size="lg"
                type="password"
                autoComplete="new-password"
              />
              <HStack align="start" spacing={3}>
                <input
                  id="signup_disclaimer_ack"
                  type="checkbox"
                  checked={signupDisclaimerAck}
                  onChange={(e) => setSignupDisclaimerAck(e.target.checked)}
                />
                <Text fontSize="sm" color="gray.600" _dark={{ color: "gray.300" }}>
                  I understand Lexi is still experimental, may be wrong, and does not replace professional advice.
                </Text>
              </HStack>
            </>
          )}

          <Button colorScheme="pink" size="lg" onClick={() => void handleAuthSubmit()} isLoading={authLoading}>
            {authMode === "signup" ? "Create account" : "Sign in"}
          </Button>

          {googleEnabled ? (
            <VStack align="stretch" spacing={2}>
              <Box ref={googleButtonRef} minH="44px" />
              {!googleReady && (
                <Text fontSize="sm" color="gray.600" _dark={{ color: "gray.300" }}>
                  Loading Google sign-in...
                </Text>
              )}
              {googleAuthLoading && (
                <Text fontSize="sm" color="gray.600" _dark={{ color: "gray.300" }}>
                  Signing in with Google...
                </Text>
              )}
            </VStack>
          ) : (
            <Button variant="outline" isDisabled>
              Continue with Google (unavailable)
            </Button>
          )}
        </VStack>
      </Box>
    </Flex>
  );
}
